import { useCallback, useEffect, useState } from "react";
import {
  apiGetFolderContents,
  type DrawingMetadata,
  type FolderInfo,
  type SharedWithMeFolder,
} from "../data/api";

import "./DrawingsDashboard.scss";

interface SharedFolderBrowserProps {
  /** Pasta raiz compartilhada por onde a navegação começa. */
  root: SharedWithMeFolder;
  /** Abre um desenho; `editable` decide se entra em modo edição ou leitura. */
  onOpenDrawing: (id: string, editable: boolean) => void;
  /** Cria uma cópia editável (fork) de um desenho e a abre. */
  onSaveCopy: (id: string) => void;
  onClose: () => void;
}

interface Crumb {
  id: string;
  name: string;
}

// Navega o conteúdo de uma pasta compartilhada (subpastas + desenhos), com
// breadcrumb. O acesso é resolvido no backend por herança; `access_role`
// vale para toda a subárvore.
export const SharedFolderBrowser = ({
  root,
  onOpenDrawing,
  onSaveCopy,
  onClose,
}: SharedFolderBrowserProps) => {
  const [stack, setStack] = useState<Crumb[]>([
    { id: root.id, name: root.name },
  ]);
  const [subfolders, setSubfolders] = useState<FolderInfo[]>([]);
  const [drawings, setDrawings] = useState<DrawingMetadata[]>([]);
  const [role, setRole] = useState<"owner" | "view" | "edit">(root.permission);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const current = stack[stack.length - 1];
  const canEdit = role !== "view";

  const load = useCallback(async (folderId: string) => {
    setLoading(true);
    setError(null);
    try {
      const contents = await apiGetFolderContents(folderId);
      setSubfolders(contents.subfolders);
      setDrawings(contents.drawings);
      setRole(contents.access_role);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao abrir a pasta.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(current.id);
  }, [load, current.id]);

  const enterFolder = (folder: FolderInfo) => {
    setStack((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const goToCrumb = (index: number) => {
    setStack((prev) => prev.slice(0, index + 1));
  };

  return (
    <div className="drawings-overlay" onClick={onClose}>
      <div
        className="shared-folder-browser"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shared-folder-browser__header">
          <nav className="shared-folder-browser__crumbs">
            {stack.map((crumb, i) => (
              <span key={crumb.id}>
                {i > 0 && <span className="shared-folder-browser__sep">/</span>}
                <button
                  className="shared-folder-browser__crumb"
                  onClick={() => goToCrumb(i)}
                  disabled={i === stack.length - 1}
                >
                  {crumb.name}
                </button>
              </span>
            ))}
          </nav>
          <button
            className="shared-folder-browser__close"
            onClick={onClose}
            aria-label="Fechar"
          >
            &times;
          </button>
        </div>

        <div className="shared-folder-browser__meta">
          Compartilhado por {root.owner_name || root.owner_email} ·{" "}
          {canEdit ? "Pode editar" : "Somente leitura"}
        </div>

        {error && <div className="share-internal-dialog__error">{error}</div>}

        {loading ? (
          <div className="dashboard-empty">Carregando…</div>
        ) : subfolders.length === 0 && drawings.length === 0 ? (
          <div className="dashboard-empty">Pasta vazia</div>
        ) : (
          <div className="dashboard-content">
            {subfolders.map((folder) => (
              <div
                key={folder.id}
                className="dashboard-folder-item"
                onDoubleClick={() => enterFolder(folder)}
                title="Abrir pasta"
              >
                <div className="dashboard-folder-item__icon">
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    stroke="none"
                  >
                    <path d="M2 6a2 2 0 012-2h5l2 2h9a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                  </svg>
                </div>
                <span className="dashboard-folder-item__name">
                  {folder.name}
                </span>
              </div>
            ))}

            {drawings.map((drawing) => (
              <div
                key={drawing.id}
                className="drawing-card drawing-card--shared"
                onDoubleClick={() => onOpenDrawing(drawing.id, canEdit)}
                title={canEdit ? "Abrir para editar" : "Abrir em modo leitura"}
              >
                <div className="drawing-card__cover">
                  {drawing.thumbnail ? (
                    <img
                      className="drawing-card__cover-img"
                      src={drawing.thumbnail}
                      alt={drawing.name}
                      draggable={false}
                    />
                  ) : (
                    <div className="drawing-card__cover-placeholder">
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <path d="M3 14l5-5 4 4 3-3 6 6" />
                        <circle
                          cx="8.5"
                          cy="8.5"
                          r="1.2"
                          fill="currentColor"
                          stroke="none"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="drawing-card__info">
                  <span className="drawing-card__name" title={drawing.name}>
                    {drawing.name}
                  </span>
                  <div className="drawing-card__shared-row">
                    <span
                      className={`drawing-card__perm-badge ${
                        canEdit ? "drawing-card__perm-badge--edit" : ""
                      }`}
                    >
                      {canEdit ? "Pode editar" : "Somente leitura"}
                    </span>
                    {canEdit && (
                      <button
                        type="button"
                        className="drawing-card__save-copy"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSaveCopy(drawing.id);
                        }}
                      >
                        Salvar cópia
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
